/**
 * Main - Punto de entrada del sistema Clinical Graph Triage.
 *
 * Wiring delgado: construye dependencias, orquesta loadPatient/scenario y
 * mueve el render loop. La lógica concreta vive en:
 *   - HandInteractionController: gestos físicos sobre el grafo
 *   - GestureHandler: traducción gesto → efecto clínico/control
 *   - ClinicalEvaluator: PatternMatcher + TriageClassifier sincrónico
 *   - AITutorUI: burbuja del tutor
 *   - ReasoningEngine / DeteriorationEngine / HypothesisField: bus de eventos
 */

import '../style.css';
import * as THREE from 'three';
import state from './state';
import { SceneManager } from '../visualization/SceneManager';
import { Patient } from '../medical/Patient';
import { PatientLoader } from '../data/PatientLoader';
import { HandController } from '../interaction/HandController';
import { GestureRecognizer } from '../interaction/GestureRecognizer';
import { clinicalGestures } from '../interaction/ClinicalGestures';
import { MedicalPhysics } from '../physics/MedicalPhysics';
import { PatternMatcher } from '../medical/PatternMatcher';
import { TriageClassifier } from '../medical/TriageClassifier';
import { ReasoningEngine } from '../medical/ReasoningEngine';
import { DeteriorationEngine } from '../medical/DeteriorationEngine';
import { HypothesisField } from '../medical/HypothesisField';
import { GestureSemantics } from '../medical/GestureSemantics';
import { ClinicalEvaluator } from '../medical/ClinicalEvaluator';
import { generateCase } from '../education/case-generator';
import { TopologyMode, AnatomicalSystem } from '../types/medical';
import { AlibabaQwenClient, qwenClient } from '../ai/AlibabaQwenClient';
import { aiAssistantPanel } from '../ai/AIAssistantPanel';
import { quizEngine } from '../education/QuizEngine';
import { timelineController } from '../education/TimelineController';
import { tutorialManager } from '../education/TutorialManager';
import { differentialPanel } from '../education/DifferentialPanel';
import { progressTracker } from '../education/ProgressTracker';
import { examOrderPanel } from '../education/ExamOrderPanel';
import { HumanModel } from '../visualization/HumanModel';
import { ClockHUD } from '../visualization/ClockHUD';
import { MouseController } from '../interaction/MouseController';
import { HandInteractionController } from '../interaction/HandInteractionController';
import { GestureHandler } from '../interaction/GestureHandler';
import { mountSidebar, SidebarAPI } from '../visualization/Sidebar';
import { HUD } from '../visualization/HUD';
import { mountLoginScreen } from '../auth/LoginScreen';
import { authService } from '../auth/AuthService';
import { recordAttempt } from '../auth/ProgressSync';
import { helpPanel } from '../education/HelpPanel';
import { legend } from '../visualization/Legend';
import { firstRunTutorial } from '../education/FirstRunTutorial';
import { caseBriefing } from '../education/CaseBriefing';
import { calculateScore, renderScoreBreakdown } from '../education/ScoringService';
import { assignmentsPanel } from '../education/AssignmentsPanel';
import { assignmentsService } from '../education/AssignmentsService';
import { triageSelector } from '../education/TriageSelector';
import { monitorDisplay } from '../visualization/MonitorDisplay';
import { massTriageRoom } from '../education/MassTriageRoom';
import { modeSelector, type CgtMode } from '../education/ModeSelector';
import { similarCasesPanel } from '../education/SimilarCasesPanel';
import { bindUndoHotkeys, undoManager } from './UndoManager';
import { liveSession, type LiveEvent } from '../live/LiveSession';
import { caseAdminPanel } from '../education/CaseAdminPanel';
import { myProgressPanel } from '../education/MyProgressPanel';
import { downloadCertificate } from '../education/CertificatePDF';
import { caseRepository } from '../data/CaseRepository';

class ClinicalGraphTriageApp {
  private sceneManager: SceneManager | null = null;
  private patient: Patient | null = null;
  private handController: HandController | null = null;
  private gestureRecognizer: GestureRecognizer | null = null;
  private physics: MedicalPhysics | null = null;
  private patternMatcher: PatternMatcher | null = null;
  private triageClassifier: TriageClassifier | null = null;
  private reasoningEngine: ReasoningEngine | null = null;
  private deteriorationEngine: DeteriorationEngine | null = null;
  private hypothesisField: HypothesisField | null = null;
  private gestureSemantics: GestureSemantics | null = null;
  private patientLoader: PatientLoader | null = null;
  private evaluator: ClinicalEvaluator | null = null;
  private handInteraction: HandInteractionController | null = null;
  private gestureHandler: GestureHandler | null = null;
  private aiClient: AlibabaQwenClient;

  private humanModel: HumanModel | null = null;
  private clockHUD: ClockHUD | null = null;
  private mouseController: MouseController | null = null;
  private hud: HUD | null = null;
  private sidebar: SidebarAPI | null = null;

  private isInitialized = false;

  constructor() {
    this.aiClient = qwenClient;
  }

  // ═════════════════════════════════════════════════════════════════════════════
  // INICIALIZACIÓN
  // ═════════════════════════════════════════════════════════════════════════════

  async initialize(): Promise<void> {
    console.log('[CGT] Initializing Clinical Graph Triage...');

    let container = document.getElementById('app');
    if (!container) {
      container = document.createElement('div');
      container.id = 'app';
      container.style.cssText = `
        position: fixed; top: 0; left: 0;
        width: 100vw; height: 100vh; overflow: hidden;
      `;
      document.body.appendChild(container);
    }

    this.hud?.showLoading();

    try {
      this.sceneManager = new SceneManager(container);
      this.handController = new HandController();
      this.gestureRecognizer = new GestureRecognizer();
      this.patternMatcher = new PatternMatcher();
      this.triageClassifier = new TriageClassifier();
      this.reasoningEngine = new ReasoningEngine(this.patternMatcher, this.triageClassifier);
      this.reasoningEngine.start();
      this.deteriorationEngine = new DeteriorationEngine();
      this.deteriorationEngine.start();
      this.hypothesisField = new HypothesisField();
      this.hypothesisField.start();
      this.gestureSemantics = new GestureSemantics();
      this.patientLoader = new PatientLoader();
      this.evaluator = new ClinicalEvaluator(this.patternMatcher, this.triageClassifier);
      this.handInteraction = new HandInteractionController(this.sceneManager);
      this.gestureHandler = new GestureHandler(this.gestureRecognizer, this.gestureSemantics, this.sceneManager);

      await this.handController.initialize();

      // Sprint 3: gestos de procedimiento (RCP/defib/pulse) → MonitorDisplay
      // se suscribe a los eventos en su propio setPatient(). Acá solo
      // alimentamos el detector con el feed de manos de cada frame.
      clinicalGestures.loadSensitivity();
      this.handController.onHandsDetected = (hands) => { clinicalGestures.feed(hands); };

      // Modelo humano 3D a la izquierda del grafo. Si la cámara está de
      // fondo (modo AR), el propio alumno es el "paciente" visualmente y
      // este stick-figure compite — se oculta en ese caso.
      this.humanModel = new HumanModel();
      this.humanModel.position.set(-8, 0, 0);
      this.humanModel.scale.setScalar(3.5);
      this.sceneManager.scene.add(this.humanModel);
      this.humanModel.enableInteraction(
        this.sceneManager.camera,
        this.sceneManager.renderer.domElement,
        this.sceneManager.controls
      );
      this.syncHumanModelVisibility();
      // Re-evaluar cuando cambia el modo cámara (observando la clase del body).
      new MutationObserver(() => this.syncHumanModelVisibility())
        .observe(document.body, { attributes: true, attributeFilter: ['class'] });

      this.setupUI();
      this.setupEventListeners();

      // Refresca /auth/me al iniciar para captar campos nuevos del esquema
      // (live_room, teacher_id) que no existían cuando el token se emitió.
      // Sin esto los usuarios pre-migración 0007 caen al room global aunque
      // tengan profesor asignado en BD.
      if (authService.isAuthenticated) {
        void authService.refreshMe();
      }

      await this.loadDefaultCase();

      this.sceneManager.startRenderLoop((deltaTime) => this.update(deltaTime));

      try {
        const savedMode = this.getSavedCameraMode();
        if (savedMode !== 'off') {
          await this.handController.startCamera(undefined, savedMode);
        }
        this.hud?.hideLoading();
      } catch {
        console.warn('[CGT] Camera not available, using mouse controls only');
        this.hud?.setLoadingText('Cámara no disponible. Usando controles de mouse.');
        setTimeout(() => this.hud?.hideLoading(), 2000);
      }

      this.updateAIStatus();
      progressTracker.createHUD();
      helpPanel.mount();
      legend.mount();
      bindUndoHotkeys();

      // Selector de modo: aparece ANTES de que el alumno vea el grafo 3D.
      // `body.cgt-awaiting-mode` oculta todo (canvas, HUD, leyenda, sidebar)
      // mientras elige. Al elegir se quita la clase y:
      //   - grafo    → se revela todo y se muestra el briefing
      //   - monitor  → se abre overlay del monitor hospitalario
      //   - triage   → se abre la sala de urgencias
      document.body.classList.add('cgt-awaiting-mode');
      this.launchChosenMode();
      // Safety net: si por alguna razón el selector no apareció en 800ms
      // y no entramos a ningún modo, quitamos la clase para que el alumno
      // NO vea pantalla negra. Mejor ver algo aunque sea el grafo.
      setTimeout(() => {
        if (document.body.classList.contains('cgt-awaiting-mode')) {
          console.warn('[CGT] Mode selector no se pintó, liberando UI');
          document.body.classList.remove('cgt-awaiting-mode');
          document.body.classList.add('cgt-mode-grafo');
        }
      }, 800);

      // Tutorial primera vez: se muestra solo si nunca lo completó, DESPUÉS
      // de que el alumno entre a un modo (no antes del selector).
      setTimeout(() => firstRunTutorial.show(), 2000);

      this.isInitialized = true;
      console.log('[CGT] Initialization complete');
    } catch (error) {
      console.error('[CGT] Initialization failed:', error);
      this.hud?.setLoadingText('Error al inicializar. Recargue la página.');
    }
  }

  // ═════════════════════════════════════════════════════════════════════════════
  // CARGA DE CASOS
  // ═════════════════════════════════════════════════════════════════════════════

  private async loadDefaultCase(): Promise<void> {
    if (!this.patientLoader) return;
    // Caso default = modo "exploración libre": el grafo arranca completo, sin
    // discovery mode. Así el alumno puede aprender la interfaz viendo todo el
    // caso y pedir IA cuando quiera (con gate ≥3 exploraciones).
    await this.loadPatient(this.patientLoader.createIAMCase(), { discovery: false });
  }

  async loadPatient(
    patientData: Parameters<PatientLoader['loadPatient']>[0],
    options: { discovery?: boolean } = {}
  ): Promise<void> {
    if (!this.patientLoader || !this.sceneManager) return;

    if (this.patient) {
      this.sceneManager.scene.remove(this.patient);
      this.patient.dispose();
      this.patient = null;
    }

    // Limpiar historial de undo: los comandos del paciente anterior no
    // tienen sentido sobre el nuevo grafo.
    undoManager.clear();

    // Limpieza defensiva: CSS2DObject labels pueden quedar huérfanos si el
    // render loop no corre antes del próximo loadPatient; hover/selected
    // pueden apuntar a nodos inexistentes en el nuevo caso.
    document.querySelectorAll('.clinical-node-label').forEach(el => el.remove());
    state.interaction.hoveredNodeId = null;
    state.interaction.selectedNodeId = null;
    state.interaction.draggedNodeId = null;

    const data = await this.patientLoader.loadPatient(patientData);
    this.patient = new Patient(data);
    this.sceneManager.scene.add(this.patient);

    const nodes = Array.from(this.patient.nodes.values());
    this.physics = new MedicalPhysics(nodes);
    this.hypothesisField?.bind(this.physics, this.patient);
    this.mouseController?.setPatient(this.patient);
    this.handInteraction?.setPatient(this.patient);
    this.handInteraction?.setPhysics(this.physics);
    this.gestureHandler?.setPatient(this.patient);
    this.evaluator?.setPatient(this.patient);
    aiAssistantPanel.setPatient(this.patient);
    monitorDisplay.setPatient(this.patient);

    state.setPatient(data);
    this.reasoningEngine?.setPatient(this.patient);

    const caseIdGuess = ClinicalEvaluator.inferCaseId(data.id);
    if (caseIdGuess) {
      this.deteriorationEngine?.setCase(this.patient, caseIdGuess);
    } else {
      this.deteriorationEngine?.clear();
    }
    this.gestureSemantics?.bind(this.patient, caseIdGuess);

    this.evaluator?.evaluate();

    this.patient.nodes.forEach(node => {
      node.createLabel();
      if (node.label) node.label.visible = state.ui.showLabels;
    });

    // Discovery mode (opt-in vía options.discovery, default true): labs,
    // imaging y dx arrancan OCULTOS. El alumno los descubre ordenando
    // exámenes o al cerrar el diagnóstico diferencial. En modo "exploración
    // libre" (loadDefaultCase, sidebar→loadCase) se desactiva para que el
    // alumno vea el grafo completo sin necesidad de examOrderPanel activo.
    const discovery = options.discovery !== false;
    if (discovery) this.patient.applyDiscoveryMode();

    // Emitir initial findings sólo de los que arrancan visibles, así el
    // DifferentialPanel arranca con la base clínica pero no "espoilea" labs.
    this.patient.nodes.forEach(node => {
      if (node.visible) state.emitFindingRevealed(node.data.id, 'initial');
    });

    this.hud?.updatePatient(this.patient);
    this.sidebar?.setActiveCase(
      data.chiefComplaint ?? data.id,
      data.triagePriority
    );

    // Nota: el briefing del grafo YA NO se muestra automáticamente al
    // cargar un caso. Se dispara desde `enterMode('grafo')` o con el botón
    // "Ver briefing". Antes interfería con el selector de modo y con Monitor/
    // Mass triage, donde el briefing tipo grafo no tiene sentido.

    console.log('[CGT] Patient loaded:', data.id);
  }

  // ═════════════════════════════════════════════════════════════════════════════
  // LOOP PRINCIPAL
  // ═════════════════════════════════════════════════════════════════════════════

  private update(deltaTime: number): void {
    state.updateSimulation(deltaTime);

    if (this.physics && !state.simulation.isPaused) {
      this.physics.hoveredNodeId = state.interaction.hoveredNodeId ?? state.interaction.draggedNodeId;
      this.physics.applyForces();
      this.physics.applyTargetPositionForces();

      const activeHand = state.getActiveHand();
      if (activeHand?.isPresent) {
        this.physics.applyMouseRepulsion(activeHand.position, 0.3);
      }

      const collisions = this.physics.checkCollisions();
      this.physics.resolveCollisions(collisions);
    }

    if (this.patient) {
      this.patient.update(deltaTime, state.simulation.globalTime);
      this.patient.applyTopology(state.topologyMode);
    }

    // Deterioro: chequea milestones del caso aunque no haya física, para que
    // el caso avance aun si la simulación está en reposo gráfico.
    this.deteriorationEngine?.tick();
    this.clockHUD?.update();

    if (this.humanModel) {
      this.humanModel.update(deltaTime);
      this.updateHumanModelConnections();
    }

    this.handInteraction?.update();
    this.gestureHandler?.tick();

    this.hud?.updateDebug();
  }

  /**
   * En modo AR (cámara de fondo) el alumno se ve a sí mismo, el stick figure
   * compite visualmente; lo ocultamos. En modo PiP o sin cámara queda visible.
   */
  private syncHumanModelVisibility(): void {
    if (!this.humanModel) return;
    const arActive = document.body.classList.contains('cgt-camera-active');
    this.humanModel.visible = !arActive;
  }

  private updateHumanModelConnections(): void {
    if (!this.humanModel || !this.patient) return;

    const nodePositions: Array<{ system: AnatomicalSystem; position: THREE.Vector3 }> = [];
    this.humanModel.clearAllHighlights();

    this.patient.nodes.forEach(node => {
      if (node.data.system && node.visible) {
        nodePositions.push({ system: node.data.system, position: node.position.clone() });
        if (node.isCritical || node.renderState.isSelected) {
          this.humanModel!.highlightSystem(node.data.system);
        }
      }
    });

    this.humanModel.updateConnections(nodePositions);
  }

  // ═════════════════════════════════════════════════════════════════════════════
  // UI / EVENTOS
  // ═════════════════════════════════════════════════════════════════════════════

  private setupUI(): void {
    if (this.triageClassifier) {
      this.hud = new HUD(this.triageClassifier);
      this.hud.mount();
    }
    if (this.deteriorationEngine) {
      this.clockHUD = new ClockHUD(this.deteriorationEngine);
    }
    this.sidebar = mountSidebar();
  }

  private setupEventListeners(): void {
    if (this.sceneManager && !this.mouseController) {
      this.mouseController = new MouseController(this.sceneManager);
    }

    // Click/selección de nodo → FINDING_REVEALED (idempotente). Uniforma el
    // bus: no importa si el nodo vino de un examen, del click o del timeline.
    state.onEvent('NODE_SELECT', (event) => {
      if (event.type === 'NODE_SELECT') {
        state.emitFindingRevealed(event.nodeId, 'click');
      }
    });

    // Discovery mode: cuando se revela un hallazgo por examen/gesto/timeline,
    // si el nodo estaba oculto, hacerlo visible en el grafo.
    state.onEvent('FINDING_REVEALED', (event) => {
      if (event.type !== 'FINDING_REVEALED') return;
      if (event.source === 'initial' || event.source === 'click') return;
      this.patient?.revealByFindingId(event.nodeId);
    });

    state.onEvent('PATTERN_DETECTED', (event) => {
      if (event.type === 'PATTERN_DETECTED') {
        this.hud?.notify(
          'Patrón detectado',
          `${event.pattern.pattern.name}: ${event.pattern.pattern.condition}`,
          'warning'
        );
      }
    });

    state.onEvent('TRIAGE_CHANGE', (event) => {
      if (event.type === 'TRIAGE_CHANGE') {
        this.hud?.notify(
          'Triage reclasificado',
          `${event.from} → ${event.to}`,
          event.to === 'immediate' ? 'warning' : 'info'
        );
      }
    });

    // HYPOTHESIS_SET lo maneja HypothesisField (campo de fuerzas + pulso).

    state.onEvent('EMERGENCY_MODE', () => {
      if (!this.patient) return;
      if (state.interaction.isEmergencyMode) {
        this.patient.filterABCNodes();
      } else {
        this.patient.showAllNodes();
      }
      // Limpiar referencias a nodos ocultos: sin esto, el selectedNodeId
      // podía apuntar a un nodo invisible y los paneles quedaban desincronizados.
      const stillVisible = (id: string | null) =>
        id ? this.patient!.getNode(id)?.visible ?? false : false;
      if (!stillVisible(state.interaction.selectedNodeId)) state.setSelectedNode(null);
      if (!stillVisible(state.interaction.hoveredNodeId)) state.setHoveredNode(null);
      if (!stillVisible(state.interaction.draggedNodeId)) state.setDraggedNode(null);
    });
  }

  // ═════════════════════════════════════════════════════════════════════════════
  // API PÚBLICA
  // ═════════════════════════════════════════════════════════════════════════════

  loadCase(caseName: 'iam' | 'sepsis' | 'epoc'): void {
    if (!this.patientLoader) return;

    const data =
      caseName === 'iam' ? this.patientLoader.createIAMCase() :
      caseName === 'sepsis' ? this.patientLoader.createSepsisCase() :
      this.patientLoader.createEPOCCase();

    // Acceso directo desde el sidebar = exploración libre, sin discovery.
    // El flujo formal de exámenes vive en startScenario/examOrderPanel.
    this.loadPatient(data, { discovery: false });

    // Si soy el presenter en una sala activa, notifico al resto.
    if (liveSession.isConnected && authService.currentUser?.role === 'teacher') {
      liveSession.send({ type: 'presenter.caseLoaded', caseId: caseName });
    }
  }

  // ═════════════════════════════════════════════════════════════════════
  // LIVE SESSION (profesor ↔ alumnos en tiempo real)
  // ═════════════════════════════════════════════════════════════════════

  joinLiveSession(roomId: string): void {
    if (!authService.isAuthenticated) {
      this.hud?.notify('No autenticado', 'Inicia sesión para unirte a la sala.', 'warning');
      return;
    }
    liveSession.connect(roomId);
    liveSession.onEvent((event) => this.handleLiveEvent(event));
    this.hud?.notify('Sala live', `Conectado a sala "${roomId}".`, 'info');
  }

  leaveLiveSession(): void {
    liveSession.disconnect();
    this.hud?.notify('Sala live', 'Desconectado.', 'info');
  }

  private handleLiveEvent(event: LiveEvent): void {
    // El profesor ignora sus propios eventos broadcast: ya los tiene en local.
    const myRole = authService.currentUser?.role;

    if (event.type === 'presenter.caseLoaded' && myRole !== 'teacher') {
      const id = event.caseId;
      if (id === 'iam' || id === 'sepsis' || id === 'epoc') {
        this.loadCase(id);
        this.hud?.notify(
          'Sala live',
          `${event.from_name ?? 'Presenter'} cargó el caso ${id.toUpperCase()}.`,
          'info'
        );
      }
    } else if (event.type === 'system.peerJoined') {
      this.hud?.notify('Sala live', `${event.display_name} entró a la sala.`, 'info');
    } else if (event.type === 'presenter.annotation') {
      this.hud?.notify(event.from_name ?? 'Presenter', event.text, 'info');
    }
  }

  /**
   * Carga un caso personalizado desde /public/data/cases/custom/<slug>.json.
   * Útil para que profesores agreguen casos sin tocar código.
   */
  async loadCustomCase(slug: string): Promise<void> {
    try {
      const data = await caseRepository.loadCustom(slug);
      await this.loadPatient(data);
      this.hud?.notify('Caso personalizado cargado', data.chiefComplaint ?? data.id, 'info');
    } catch (error) {
      const msg = error instanceof Error ? error.message : String(error);
      this.hud?.notify('Error al cargar caso', msg, 'critical');
      console.error('[CGT] loadCustomCase failed:', error);
    }
  }

  /** Carga desde una URL arbitraria (ej. backend propio). */
  async loadCaseFromUrl(url: string): Promise<void> {
    try {
      const data = await caseRepository.loadFromUrl(url);
      await this.loadPatient(data);
      this.hud?.notify('Caso cargado', data.chiefComplaint ?? data.id, 'info');
    } catch (error) {
      const msg = error instanceof Error ? error.message : String(error);
      this.hud?.notify('Error al cargar caso', msg, 'critical');
      console.error('[CGT] loadCaseFromUrl failed:', error);
    }
  }

  /** Reabre el tutorial de primera vez (desde Ayuda u opción del alumno). */
  replayTutorial(): void {
    firstRunTutorial.show(true);
  }

  /** Reabre el briefing del caso activo. Útil si el alumno lo cerró sin leer. */
  replayBriefing(): void {
    if (!this.patient) return;
    caseBriefing.show(this.patient.data, undefined, true);
  }

  /** Abre el panel de tareas asignadas al alumno. */
  openMyAssignments(): void {
    assignmentsPanel.openForStudent();
  }

  /**
   * Lanza el modo elegido por el alumno (o el último guardado). Se llama
   * al finalizar init y desde el sidebar con "Cambiar modo".
   */
  private launchChosenMode(force = false): void {
    modeSelector.show((mode) => this.enterMode(mode), force);
  }

  private enterMode(mode: CgtMode): void {
    // Al elegir modo se quita la capa que escondía todo. Para monitor/triage
    // igual no se ve el grafo porque sus overlays full-screen lo tapan,
    // pero el estado DOM queda consistente.
    document.body.classList.remove('cgt-awaiting-mode');
    // Flags específicos para el CSS que oculta el grafo en modos no-estudio.
    document.body.classList.toggle('cgt-mode-monitor', mode === 'monitor');
    document.body.classList.toggle('cgt-mode-triage', mode === 'triage-sala');
    document.body.classList.toggle('cgt-mode-grafo', mode === 'grafo');

    switch (mode) {
      case 'triage-sala':
        if (this.monitorOpen) this.closeMonitor();
        this.openMassTriage(6);
        break;
      case 'monitor':
        this.openMonitor();
        break;
      case 'grafo':
        // El grafo es el modo de estudio. Aquí sí tiene sentido mostrar el
        // briefing si hay un paciente cargado y no se completó aún.
        if (this.monitorOpen) this.closeMonitor();
        if (this.patient && !state.userProposedDx) {
          caseBriefing.show(this.patient.data);
        }
        break;
    }
  }

  /** Expone la entrada al selector desde UI ("Cambiar modo"). */
  openModeSelector(): void {
    this.launchChosenMode(true);
  }

  /**
   * Toggle del Monitor Mode (vista hospitalaria multiparamétrica).
   * Se monta como overlay absoluto encima del canvas Three.js, el grafo
   * sigue corriendo debajo por si el alumno vuelve al modo exploración.
   */
  private monitorOpen = false;

  toggleMonitor(): void {
    if (this.monitorOpen) this.closeMonitor();
    else this.openMonitor();
  }

  openMonitor(): void {
    if (this.monitorOpen) return;
    if (!this.patient) {
      this.hud?.notify('Sin paciente', 'Carga un caso primero.', 'warning');
      return;
    }
    const container = document.getElementById('app');
    if (!container) return;
    // Refresca /auth/me para captar reasignaciones de profesor mid-sesión:
    // si el profe asignó al alumno hace minutos, su live_room cambió y
    // necesitamos publicar al room correcto desde el primer mensaje.
    if (authService.isAuthenticated) {
      void authService.refreshMe();
    }
    // Briefing clínico ANTES de montar el monitor (O4+A1): el alumno entra
    // al box con una consigna explícita (paciente + motivo + objetivo), no
    // frío inferido desde vitales.
    if (!state.userProposedDx) {
      caseBriefing.show(this.patient.data);
    }
    monitorDisplay.setPatient(this.patient);
    monitorDisplay.mount(container);
    this.monitorOpen = true;
    this.sidebar?.collapse();
    // O7: si la cámara está apagada, encenderla en PiP para que los gestos
    // de procedimiento (RCP/defib/pulso) y la auscultación por palma estén
    // disponibles sin que el alumno tenga que buscarlo en el sidebar.
    if (this.getSavedCameraMode() === 'off' && this.handController) {
      void this.setCameraMode('pip').catch(e => console.warn('[Monitor] auto-camera falló:', e));
    }
  }

  closeMonitor(): void {
    if (!this.monitorOpen) return;
    monitorDisplay.unmount();
    this.monitorOpen = false;
    this.sidebar?.expand();
    // Al cerrar el monitor volvemos al selector de modo. Si el alumno quiere
    // seguir en el mismo caso puede elegir "grafo" o repetir monitor.
    this.backToModeSelector();
  }

  private backToModeSelector(): void {
    document.body.classList.remove('cgt-mode-monitor', 'cgt-mode-triage', 'cgt-mode-grafo');
    document.body.classList.add('cgt-awaiting-mode');
    this.launchChosenMode(true);
  }

  /**
   * Mass Triage Mode — sala de urgencias con N pacientes simultáneos.
   * No requiere un caso cargado: genera pacientes del pool interno.
   */
  openMassTriage(n = 6): void {
    massTriageRoom.open(n, () => this.backToModeSelector());
    // O7: misma lógica que openMonitor — encender cámara PiP si estaba off.
    if (this.getSavedCameraMode() === 'off' && this.handController) {
      void this.setCameraMode('pip').catch(e => console.warn('[MassTriage] auto-camera falló:', e));
    }
  }

  /** Abre el panel de asignaciones del profesor (crear/listar/eliminar). */
  openTeacherAssignments(): void {
    assignmentsPanel.openForTeacher();
  }

  /**
   * Muestra el breakdown de puntaje al completar un caso. Overlay modal
   * con el total + lista de ítems (ganados/perdidos/penalidades). Cierra
   * al clickear afuera o con "Entendido".
   */
  private showScoreBreakdown(result: ReturnType<typeof calculateScore>): void {
    // Inyectar estilos una vez
    if (!document.getElementById('cgt-score-styles')) {
      const style = document.createElement('style');
      style.id = 'cgt-score-styles';
      style.textContent = `
        #cgt-score-overlay {
          position: fixed; inset: 0; z-index: 13000;
          display: flex; align-items: center; justify-content: center;
          background: rgba(0,0,0,0.7); backdrop-filter: blur(6px);
          opacity: 0; transition: opacity 0.25s;
        }
        #cgt-score-overlay.cgt-score-visible { opacity: 1; }
        #cgt-score-overlay .cgt-score-card {
          width: min(520px, 92vw); max-height: 88vh; overflow-y: auto;
          background: #0a0a0a; border: 0.5px solid rgba(255,255,255,0.1);
          border-radius: 18px; padding: 28px 32px;
          color: #f5f5f7; box-shadow: 0 24px 80px rgba(0,0,0,0.6);
          font-family: -apple-system, BlinkMacSystemFont, 'Inter', sans-serif;
        }
        #cgt-score-overlay .cgt-score-title {
          font-size: 10.5px; font-weight: 600; letter-spacing: 1.5px;
          text-transform: uppercase; color: #6e6e73; margin-bottom: 8px;
        }
        #cgt-score-overlay .cgt-score-total {
          font-size: 52px; font-weight: 800; letter-spacing: -1.5px;
          color: #f5f5f7; line-height: 1;
        }
        #cgt-score-overlay .cgt-score-total span {
          font-size: 20px; color: #6e6e73; margin-left: 4px;
        }
        #cgt-score-overlay .cgt-score-items {
          list-style: none; padding: 0; margin: 20px 0 0;
          display: flex; flex-direction: column; gap: 6px;
        }
        #cgt-score-overlay .cgt-score-items li {
          padding: 10px 12px; border-radius: 8px;
          display: grid; grid-template-columns: 1fr auto; align-items: baseline; gap: 4px 12px;
          font-size: 13px;
        }
        #cgt-score-overlay .cgt-score-items small {
          grid-column: 1 / -1; font-size: 11px; color: #8e8e93; margin-top: 2px;
        }
        #cgt-score-overlay .cgt-score-label { color: #d1d1d6; }
        #cgt-score-overlay .cgt-score-pts {
          font-family: 'JetBrains Mono', monospace; font-weight: 700; font-size: 14px;
        }
        #cgt-score-overlay .cgt-score-ok { background: rgba(48,209,88,0.08); }
        #cgt-score-overlay .cgt-score-ok .cgt-score-pts { color: #30D158; }
        #cgt-score-overlay .cgt-score-miss { background: rgba(142,142,147,0.06); opacity: 0.7; }
        #cgt-score-overlay .cgt-score-miss .cgt-score-pts { color: #6e6e73; }
        #cgt-score-overlay .cgt-score-bad { background: rgba(255,69,58,0.1); }
        #cgt-score-overlay .cgt-score-bad .cgt-score-pts { color: #FF6961; }
        #cgt-score-overlay .cgt-score-close {
          margin-top: 20px; width: 100%; padding: 12px;
          background: linear-gradient(135deg, #2997FF, #BF5AF2);
          color: #fff; border: none; border-radius: 10px;
          font-weight: 700; font-size: 14px; cursor: pointer; font-family: inherit;
        }
      `;
      document.head.appendChild(style);
    }

    const overlay = document.createElement('div');
    overlay.id = 'cgt-score-overlay';
    overlay.innerHTML = `
      <div class="cgt-score-card">
        <div class="cgt-score-title">🎯 Rúbrica del caso</div>
        ${renderScoreBreakdown(result)}
        <button type="button" class="cgt-score-close">Entendido</button>
      </div>
    `;
    const close = (): void => {
      overlay.classList.remove('cgt-score-visible');
      setTimeout(() => overlay.remove(), 200);
    };
    overlay.addEventListener('click', (e) => {
      if (e.target === overlay) close();
    });
    overlay.querySelector<HTMLButtonElement>('.cgt-score-close')?.addEventListener('click', close);
    document.body.appendChild(overlay);
    requestAnimationFrame(() => overlay.classList.add('cgt-score-visible'));
  }

  /** Abre "Mi progreso" — analytics personal del alumno. */
  openMyProgress(): void {
    if (!authService.isAuthenticated) {
      this.hud?.notify('No autenticado', 'Inicia sesión para ver tu progreso.', 'warning');
      return;
    }
    void myProgressPanel.open();
  }

  /** Abre el modal de administración de casos (solo profesor). */
  openCaseAdmin(): void {
    if (!caseAdminPanel.isAvailable()) {
      this.hud?.notify('No autorizado', 'Solo profesores pueden administrar casos.', 'warning');
      return;
    }
    caseAdminPanel.open();
  }

  /** Carga un caso desde un objeto ya parseado (admin UI). */
  async loadCaseFromObject(data: unknown): Promise<void> {
    try {
      const patient = caseRepository.loadFromObject(data);
      await this.loadPatient(patient);
      this.hud?.notify('Caso cargado', patient.chiefComplaint ?? patient.id, 'info');
    } catch (error) {
      const msg = error instanceof Error ? error.message : String(error);
      this.hud?.notify('Error al cargar caso', msg, 'critical');
      console.error('[CGT] loadCaseFromObject failed:', error);
    }
  }

  /**
   * Abre el Asistente IA unificado. Los 3 tabs (Diagnóstico, Patrones, Tutor)
   * comparten contexto: los hallazgos revelados alimentan todos.
   *
   * El sidebar y el panel compiten por el mismo espacio (ambos en la derecha),
   * así que colapsamos el sidebar mientras el panel está abierto y lo
   * restauramos al cerrar — sólo si el alumno lo tenía expandido (respetamos
   * su preferencia si ya lo había colapsado manualmente).
   */
  openAIAssistant(tab: 'diagnostico' | 'patrones' | 'tutor' = 'diagnostico'): void {
    if (!this.patient) {
      this.hud?.notify('Sin paciente', 'Carga un caso primero.', 'warning');
      return;
    }
    const wasExpanded = this.sidebar && !this.sidebar.isCollapsed();
    if (wasExpanded) {
      this.sidebar?.collapse();
      aiAssistantPanel.onClose = () => this.sidebar?.expand();
    }
    aiAssistantPanel.open(tab);
  }

  /** Atajos compatibles con botones previos del sidebar. */
  analyzeWithAI(): void { this.openAIAssistant('diagnostico'); }
  matchFindings(): void { this.openAIAssistant('patrones'); }
  askTutor(): void { this.openAIAssistant('tutor'); }

  async startRandomScenario(
    base: 'iam' | 'sepsis' | 'epoc',
    mode: 'classroom' | 'individual' = 'individual'
  ): Promise<void> {
    const generatedId = generateCase(base);
    await this.startScenario(generatedId, mode);
  }

  async startScenario(scenarioId: string, mode: 'classroom' | 'individual' = 'classroom'): Promise<void> {
    // En sesión activa el sidebar es ruido: compite con examenes y diferencial.
    this.sidebar?.collapse();

    await examOrderPanel.startScenario(
      scenarioId,
      async (patientData) => {
        await this.loadPatient(patientData);
        if (this.patient) examOrderPanel.setPatient(this.patient);
      },
      mode,
      () => this.evaluator?.evaluate()
    );

    const baseId = ClinicalEvaluator.baseCaseId(scenarioId);
    const scenarioStart = Date.now();
    // Paso 1: triage (si aún no propuesto en este caso).
    const expectedTriage = this.patient?.data.triagePriority ?? 'delayed';
    triageSelector.show(expectedTriage, () => {
      differentialPanel.start(baseId, (_dxName, isCorrect) => {
      const elapsedSec = Math.max(0, Math.round((Date.now() - scenarioStart) / 1000));
      progressTracker.recordDiagnosis(isCorrect, Date.now());
      progressTracker.recordCaseComplete();

      // Rubric: dx correcto, triage correcto (comparando elección vs oficial),
      // eficiencia, razonamiento propio. Breakdown se muestra al alumno.
      const triageCorrect = state.userProposedTriage === expectedTriage;
      const scoreResult = calculateScore({
        diagnosisCorrect: isCorrect,
        triageCorrect,
        durationSeconds: elapsedSec,
        consultedAIBeforeDx: state.userOpenedAIBeforeDx,
      });
      const score = scoreResult.total;

      void recordAttempt({
        case_id: scenarioId,
        diagnosis_correct: isCorrect,
        triage_correct: triageCorrect,
        score,
        duration_seconds: elapsedSec,
      });

      // Si el alumno arrancó este caso desde "Mis tareas", cerramos el loop:
      // marcar la asignación como done con el score. El profesor la ve en su
      // lista con status "Completada" y score.
      if (state.activeAssignmentId !== null) {
        void assignmentsService
          .markDone(state.activeAssignmentId, score)
          .catch(err => console.warn('[assign] markDone failed:', err));
        state.activeAssignmentId = null;
      }

      this.showScoreBreakdown(scoreResult);

      if (isCorrect && this.patient) {
        this.patient.nodes.forEach(node => {
          if (node.data.type === 'diagnosis') this.patient!.revealNode(node.data.id);
        });
        void similarCasesPanel.showForCase(baseId);

        this.lastCompletion = {
          caseId: scenarioId,
          caseLabel: this.patient.data.chiefComplaint ?? scenarioId,
          diagnosisCorrect: isCorrect,
          triageCorrect,
          score,
          durationSeconds: elapsedSec,
          completedAt: new Date(),
        };
        this.hud?.notify(
          '¡Caso completado!',
          `Puntaje ${score}/100. Descargá tu certificado desde el menú lateral.`,
          'info'
        );
      }
      });
    });
  }

  /**
   * Descarga un certificado PDF del último caso completado con éxito.
   * Solo habilitado después de un diagnóstico correcto en la sesión actual.
   */
  private lastCompletion: Parameters<typeof downloadCertificate>[0] | null = null;

  downloadCertificate(): void {
    if (!this.lastCompletion) {
      this.hud?.notify('Sin certificado', 'Completa un caso correctamente primero.', 'warning');
      return;
    }
    if (!authService.isAuthenticated) {
      this.hud?.notify('No autenticado', 'Inicia sesión para emitir el certificado.', 'warning');
      return;
    }
    downloadCertificate(this.lastCompletion);
  }

  startDifferential(caseId?: string): void {
    const targetCase = caseId || this.detectCurrentCase();
    if (!this.patient) return;
    const expectedTriage = this.patient.data.triagePriority;
    this.patient.hideNodesByType('diagnosis');

    const openedAt = Date.now();
    // Paso 1: triage (si aún no propuesto). Después sigue con DDx.
    triageSelector.show(expectedTriage, () => {
      differentialPanel.start(targetCase, (dxName, isCorrect) => {
        const elapsedSec = Math.max(0, Math.round((Date.now() - openedAt) / 1000));
        const triageCorrect = state.userProposedTriage === expectedTriage;

        const scoreResult = calculateScore({
          diagnosisCorrect: isCorrect,
          triageCorrect,
          durationSeconds: elapsedSec,
          consultedAIBeforeDx: state.userOpenedAIBeforeDx,
        });
        const score = scoreResult.total;

        void recordAttempt({
          case_id: targetCase,
          diagnosis_correct: isCorrect,
          triage_correct: triageCorrect,
          score,
          duration_seconds: elapsedSec,
        });

        if (state.activeAssignmentId !== null) {
          void assignmentsService
            .markDone(state.activeAssignmentId, score)
            .catch(err => console.warn('[assign] markDone failed:', err));
          state.activeAssignmentId = null;
        }

        if (isCorrect && this.patient) {
          this.patient.nodes.forEach(node => {
            if (node.data.type === 'diagnosis') this.patient!.revealNode(node.data.id);
          });
        }

        this.showScoreBreakdown(scoreResult);
        this.hud?.notify(
          isCorrect ? 'Diagnóstico correcto' : 'Revisa tu razonamiento',
          `${dxName} · ${score}/100`,
          isCorrect ? 'info' : 'warning'
        );
      });
    });
  }

  startTimeline(caseId?: string): void {
    const targetCase = caseId || this.detectCurrentCase();
    if (!this.patient) return;
    timelineController.start(targetCase, this.patient);
  }

  startTutorial(): void {
    if (!this.patient || !this.sceneManager) return;
    tutorialManager.start(this.patient, this.sceneManager);
  }

  private detectCurrentCase(): string {
    if (this.patient?.data.id === 'PT-2024-001') return 'iam';
    if (this.patient?.data.id === 'PT-2024-002') return 'sepsis';
    return 'epoc';
  }

  startQuiz(caseId?: string): void {
    const targetCase = caseId || this.detectCurrentCase();
    if (!this.patient) return;

    quizEngine.start(targetCase, this.patient, (score, maxScore) => {
      this.hud?.notify(
        'Quiz completado',
        `Puntuación: ${Math.round((score / maxScore) * 100)}%`,
        score / maxScore >= 0.7 ? 'info' : 'warning'
      );
    });
  }

  resetView(): void {
    if (!this.sceneManager) return;
    this.sceneManager.resetCamera();
    if (this.patient) {
      this.patient.nodes.forEach(node => {
        node.mesh.scale.setScalar(1);
        const r = node.getBoundingRadius() * 3;
        node.glow.scale.set(r, r, 1);
      });
    }
  }

  setTopology(mode: number): void {
    const modes: TopologyMode[] = [
      'anatomical',
      'fisiopathological',
      'temporal_spiral',
      'differential_competitive',
    ];
    state.setTopologyMode(modes[mode] || 'anatomical');
  }

  /**
   * Modo cámara preferido por el usuario, persistido en localStorage.
   * 'background' por defecto (AR). El botón del sidebar llama setCameraMode.
   */
  private getSavedCameraMode(): 'background' | 'pip' | 'off' {
    const saved = localStorage.getItem('cgt_camera_mode');
    if (saved === 'pip' || saved === 'off' || saved === 'background') return saved;
    // Default OFF: la cámara con AR detrás del grafo hacía sentir la app
    // "bonita pero no hospitalaria". El alumno la activa explícitamente
    // cuando quiere usar gestos clínicos (Monitor Mode).
    return 'off';
  }

  async setCameraMode(mode: 'background' | 'pip' | 'off'): Promise<void> {
    localStorage.setItem('cgt_camera_mode', mode);
    await this.handController?.setCameraMode(mode);
    this.syncHumanModelVisibility();
    this.hud?.notify('Cámara', `Modo: ${mode === 'background' ? 'AR (fondo)' : mode === 'pip' ? 'PiP (esquina)' : 'apagada'}`, 'info');
  }

  private updateAIStatus(): void {
    const statusEl = document.getElementById('ai-status');
    if (!statusEl) return;

    const status = this.aiClient.getStatus();
    if (status.available) {
      statusEl.textContent = '✅ Motor de IA conectado';
      statusEl.style.color = '#10B981';
    } else {
      statusEl.textContent = '🧪 IA en modo preview (local)';
      statusEl.style.color = '#F59E0B';
    }
  }

  dispose(): void {
    this.sceneManager?.dispose();
    this.handController?.stop();
    this.patient?.dispose();
    aiAssistantPanel.close();
  }
}

// Exponer app globalmente para el landing HTML (enterApp → window.app.loadCase).
(window as unknown as Record<string, unknown>).app = null;

document.addEventListener('DOMContentLoaded', () => {
  // Login bloquea el arranque hasta autenticarse (o si ya hay sesión activa).
  // Cuando el backend no está disponible, el alumno puede saltarse el login
  // recargando sin llamar a nada: el landing seguirá funcionando pero sin
  // persistencia de progreso.
  mountLoginScreen((user) => {
    console.log('[CGT] User authenticated:', user.email, `(${user.role})`);
    const app = new ClinicalGraphTriageApp();
    (window as unknown as Record<string, unknown>).app = app;
    app.initialize();
  });
});

// Permite que cualquier panel tenga acceso directo para desloguear.
(window as unknown as Record<string, unknown>).cgtLogout = () => {
  authService.logout();
  location.reload();
};

export default ClinicalGraphTriageApp;
